import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { jobApi } from '../../api/jobApi'
import { referralApi } from '../../api/referralApi'
import { Job, JobComment } from '../../types'
import ApplyModal from '../../components/referral/ApplyModal'
import { formatDistanceToNow } from 'date-fns'
import { toast } from 'react-hot-toast'
import {
  Heart, MessageCircle, MapPin, Briefcase, User,
  ArrowLeft, Send, Loader2, ExternalLink
} from 'lucide-react'
import { clsx } from 'clsx'

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [job, setJob] = useState<Job | null>(null)
  const [comments, setComments] = useState<JobComment[]>([])
  const [commentText, setCommentText] = useState('')
  const [loading, setLoading] = useState(true)
  const [commentLoading, setCommentLoading] = useState(false)
  const [applyOpen, setApplyOpen] = useState(false)
  const jobId = Number(id)

  useEffect(() => {
    loadJob()
    loadComments()
  }, [jobId])

  const loadJob = async () => {
    try {
      const { data } = await jobApi.getById(jobId)
      setJob(data.data!)
    } catch {
      toast.error('Failed to load job')
    } finally {
      setLoading(false)
    }
  }

  const loadComments = async () => {
    try {
      const { data } = await jobApi.getComments(jobId)
      setComments(data.data?.content || [])
    } catch {}
  }

  const handleLike = async () => {
    if (!job) return
    try {
      await jobApi.toggleLike(jobId)
      setJob((prev) =>
        prev
          ? {
              ...prev,
              likedByMe: !prev.likedByMe,
              likesCount: prev.likedByMe ? prev.likesCount - 1 : prev.likesCount + 1,
            }
          : prev
      )
    } catch {
      toast.error('Failed to update like')
    }
  }

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim()) return
    setCommentLoading(true)
    try {
      await jobApi.addComment(jobId, commentText)
      setCommentText('')
      loadComments()
      setJob((prev) => (prev ? { ...prev, commentsCount: prev.commentsCount + 1 } : prev))
    } catch {
      toast.error('Failed to post comment')
    } finally {
      setCommentLoading(false)
    }
  }

  const handleApplySubmit = async (coverLetter: string, resumeFile: File | null) => {
    if (!job) return
    const { data } = await referralApi.apply(job.id, coverLetter)
    const application = data.data!
    if (resumeFile && application.resumeUploadUrl) {
      await fetch(application.resumeUploadUrl, {
        method: 'PUT',
        body: resumeFile,
        headers: { 'Content-Type': resumeFile.type },
      })
    }
    toast.success('Application submitted!')
    setApplyOpen(false)
    setJob((prev) => prev ? { ...prev, appliedByMe: true } : prev)
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    )
  }

  if (!job) return null

  const isFree = job.referralFeePaise === 0
  const feeRupees = job.referralFeePaise / 100

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 no-underline">
        <ArrowLeft size={16} /> Back to feed
      </Link>

      <div className="card">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
            {job.company.logoUrl ? (
              <img src={job.company.logoUrl} alt={job.company.name} className="w-full h-full object-cover" />
            ) : (
              <Briefcase size={24} className="text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold text-gray-900">{job.title}</h1>
                <p className="text-gray-600">{job.company.name}</p>
              </div>
              <span className={clsx('badge shrink-0', isFree ? 'badge-green' : 'badge-blue')}>
                {isFree ? 'Free' : `₹${feeRupees} fee`}
              </span>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
          {job.location && (
            <span className="flex items-center gap-1.5"><MapPin size={15} />{job.location}</span>
          )}
          {job.jobType && (
            <span className="flex items-center gap-1.5"><Briefcase size={15} />{job.jobType}</span>
          )}
          {(job.experienceMin !== undefined || job.experienceMax !== undefined) && (
            <span className="flex items-center gap-1.5">
              <User size={15} />{job.experienceMin}–{job.experienceMax} years experience
            </span>
          )}
        </div>

        {/* Skills */}
        {job.skills.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {job.skills.map((skill) => (
              <span key={skill} className="badge badge-gray">{skill}</span>
            ))}
          </div>
        )}

        {/* Description */}
        <div className="mt-4">
          <h2 className="font-semibold text-gray-900 mb-2">Job Description</h2>
          <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{job.description}</p>
        </div>

        {/* Attachments */}
        {job.attachments && job.attachments.length > 0 && (
          <div className="mt-4">
            <h2 className="font-semibold text-gray-900 mb-2">Attachments</h2>
            <div className="space-y-2">
              {job.attachments.map((att) => (
                <a
                  key={att.id}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary-600 hover:underline"
                >
                  <ExternalLink size={14} /> {att.filename}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLike}
              className={clsx('flex items-center gap-1.5 text-sm transition-colors', job.likedByMe ? 'text-red-500' : 'text-gray-500 hover:text-red-400')}
            >
              <Heart size={18} fill={job.likedByMe ? 'currentColor' : 'none'} />
              {job.likesCount}
            </button>
            <span className="flex items-center gap-1.5 text-sm text-gray-500">
              <MessageCircle size={18} /> {job.commentsCount}
            </span>
            <span className="text-sm text-gray-400">
              {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
            </span>
          </div>
          {job.appliedByMe ? (
            <button
              disabled
              className="flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold cursor-not-allowed"
              style={{ background: '#EDE9FE', color: '#7C3AED', border: '1px solid #8B5CF6' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Applied
            </button>
          ) : (
            <button onClick={() => setApplyOpen(true)} className="btn-primary">
              {isFree ? 'Refer Me (Free)' : `Refer Me (₹${feeRupees})`}
            </button>
          )}
        </div>
      </div>

      {/* Comments section */}
      <div id="comments" className="card mt-4">
        <h2 className="font-semibold text-gray-900 mb-4">Comments ({job.commentsCount})</h2>

        {/* Comment form */}
        <form onSubmit={handleComment} className="flex gap-3 mb-6">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a comment..."
            className="input flex-1"
          />
          <button type="submit" disabled={commentLoading || !commentText.trim()} className="btn-primary px-4">
            {commentLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </form>

        {/* Comments list */}
        <div className="space-y-4">
          {comments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No comments yet. Be the first!</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                  <span className="text-xs font-medium text-primary-700">
                    {comment.user.displayName?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-gray-900">{comment.user.displayName}</span>
                    <span className="text-xs text-gray-400">
                      {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5">{comment.content}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {applyOpen && (
        <ApplyModal
          job={job}
          onClose={() => setApplyOpen(false)}
          onSubmit={handleApplySubmit}
        />
      )}
    </div>
  )
}
